import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/Page";
import { ErrorState } from "@/components/common/States";
import { ClassFormDialog } from "@/components/classes/ClassFormDialog";
import { ClassDetailDialog } from "@/components/classes/ClassDetailDialog";
import { useFetch } from "@/hooks/useFetch";
import { addDays, MONTHS, startOfWeek, toISO, todayISO, WEEKDAYS_SHORT, WEEKDAYS_LONG, endTime } from "@/lib/format";

const CHIP = {
  agendada: "border-l-blue-500 bg-blue-500/10",
  confirmada: "border-l-primary bg-primary/10",
  realizada: "border-l-emerald-500 bg-emerald-500/10",
  cancelada: "border-l-rose-500 bg-rose-500/10 line-through opacity-60",
  faltou: "border-l-orange-500 bg-orange-500/10",
};
const VIEWS = [["dia", "Dia"], ["semana", "Semana"], ["mes", "Mês"]];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 7);

const Chip = ({ c, onOpen, compact }) => (
  <button onClick={(e) => { e.stopPropagation(); onOpen(c.id); }}
    className={`block w-full truncate rounded-md border-l-[3px] px-2 py-1 text-left text-xs transition-transform hover:scale-[1.02] ${CHIP[c.status]}`}
    data-testid={`agenda-class-${c.id}`}>
    <span className="font-mono font-semibold">{c.start_time}</span> {compact ? c.student_name.split(" ")[0] : `${c.subject} — ${c.student_name}`}
  </button>
);

function range(view, cursor) {
  if (view === "dia") return [cursor, cursor];
  if (view === "semana") {
    const s = startOfWeek(cursor);
    return [s, addDays(s, 6)];
  }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const s = startOfWeek(first);
  return [s, addDays(s, 41)];
}

export default function Agenda() {
  const [view, setView] = useState("semana");
  const [cursor, setCursor] = useState(new Date());
  const [create, setCreate] = useState(null);
  const [detail, setDetail] = useState(null);
  const [start, end] = range(view, cursor);
  const { data, error, reload } = useFetch(`/classes?start=${toISO(start)}&end=${toISO(end)}`);

  const byDate = useMemo(() => {
    const m = {};
    (data || []).forEach((c) => (m[c.date] = m[c.date] || []).push(c));
    return m;
  }, [data]);

  const step = (dir) => {
    const d = new Date(cursor);
    if (view === "dia") d.setDate(d.getDate() + dir);
    else if (view === "semana") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  const title = view === "mes" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "semana" ? `${start.getDate()}/${start.getMonth() + 1} – ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`
    : `${WEEKDAYS_LONG[(cursor.getDay() + 6) % 7]}, ${cursor.getDate()} de ${MONTHS[cursor.getMonth()]}`;

  const days = Array.from({ length: view === "mes" ? 42 : view === "semana" ? 7 : 1 }, (_, i) => addDays(start, i));
  const today = todayISO();

  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Agenda" subtitle="Organize suas aulas por dia, semana ou mês"
        actions={<Button onClick={() => setCreate({ date: toISO(cursor) })} data-testid="agenda-new-class-btn"><Plus className="mr-2 h-4 w-4" />Nova aula</Button>} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => step(-1)} data-testid="agenda-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="agenda-today-btn">Hoje</Button>
          <Button variant="outline" size="icon" onClick={() => step(1)} data-testid="agenda-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <span className="ml-2 font-heading font-semibold" data-testid="agenda-title">{title}</span>
        </div>
        <div className="inline-flex rounded-lg border bg-card p-1">
          {VIEWS.map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`agenda-view-${k}`}>{label}</button>
          ))}
        </div>
      </div>
      {error && <ErrorState error={error} onRetry={reload} />}

      {view === "mes" && (
        <div className="overflow-hidden rounded-xl border bg-card" data-testid="agenda-month-view">
          <div className="grid grid-cols-7 border-b bg-muted/50 text-center text-xs font-semibold text-muted-foreground">{WEEKDAYS_SHORT.map((d) => <div key={d} className="py-2">{d}</div>)}</div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const iso = toISO(d);
              const list = byDate[iso] || [];
              return (
                <div key={iso} onClick={() => { setCursor(d); setView("dia"); }} className={`min-h-[72px] cursor-pointer border-b border-r p-1 transition-colors hover:bg-muted/40 sm:min-h-[104px] sm:p-1.5 ${d.getMonth() !== cursor.getMonth() ? "opacity-40" : ""}`} data-testid={`agenda-month-day-${iso}`}>
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${iso === today ? "bg-primary text-primary-foreground" : ""}`}>{d.getDate()}</span>
                  <div className="mt-1 hidden space-y-1 sm:block">{list.slice(0, 3).map((c) => <Chip key={c.id} c={c} onOpen={setDetail} compact />)}{list.length > 3 && <p className="text-[10px] text-muted-foreground">+{list.length - 3}</p>}</div>
                  {list.length > 0 && <div className="mt-1 flex justify-center sm:hidden"><span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">{list.length}</span></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "semana" && (
        <div className="grid gap-3 md:grid-cols-7" data-testid="agenda-week-view">
          {days.map((d, i) => {
            const iso = toISO(d);
            return (
              <div key={iso} className={`rounded-xl border bg-card p-3 ${iso === today ? "border-primary" : ""}`} data-testid={`agenda-week-day-${iso}`}>
                <button onClick={() => { setCursor(d); setView("dia"); }} className="mb-2 flex w-full items-baseline justify-between md:block md:text-center">
                  <span className="text-xs font-semibold uppercase text-muted-foreground">{WEEKDAYS_SHORT[i]}</span>
                  <span className={`ml-2 font-mono text-lg font-semibold md:ml-0 md:block ${iso === today ? "text-primary" : ""}`}>{d.getDate()}</span>
                </button>
                <div className="space-y-1.5">
                  {(byDate[iso] || []).map((c) => <Chip key={c.id} c={c} onOpen={setDetail} compact />)}
                  <button onClick={() => setCreate({ date: iso })} className="w-full rounded-md border border-dashed py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid={`agenda-add-${iso}`}>+</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "dia" && (
        <div className="rounded-xl border bg-card" data-testid="agenda-day-view">
          {HOURS.map((h) => {
            const hh = String(h).padStart(2, "0");
            const list = (byDate[toISO(cursor)] || []).filter((c) => c.start_time.startsWith(hh));
            return (
              <div key={h} className="flex min-h-[56px] border-b last:border-0">
                <div className="w-16 shrink-0 border-r py-2 text-center font-mono text-xs text-muted-foreground">{hh}:00</div>
                <div onClick={() => setCreate({ date: toISO(cursor), start_time: `${hh}:00` })} className="flex-1 cursor-pointer space-y-1 p-1.5 transition-colors hover:bg-muted/30" data-testid={`agenda-slot-${hh}`}>
                  {list.map((c) => (
                    <div key={c.id}><Chip c={c} onOpen={setDetail} /><span className="ml-2 text-[11px] text-muted-foreground">até {endTime(c.start_time, c.duration)}</span></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ClassFormDialog open={!!create} onOpenChange={(o) => !o && setCreate(null)} defaults={create || {}} onSaved={reload} />
      <ClassDetailDialog open={!!detail} lessonId={detail} onOpenChange={(o) => !o && setDetail(null)} onChanged={reload} />
    </div>
  );
}
